import { IApiRequestOptions } from '@import-io/doc-sdk';
import { IHistoryItem } from '_/common/history/history.types';
import { IReadme, IReadmeVersion } from '_/common/types/readme.types';
import { IListResponse } from '_/types/common.types';
import { IProject } from '_/types/projects/project.types';
import zeusSdk from '_/utils/SDKs';

export const fetchAllProjects = async (): Promise<IProject[]> => {
  try {
    const options = {
      queryParameters: {
        sort: 'name',
        sortDirection: 'ASC',
      },
    };
    const projects = await zeusSdk.projects.listAll(options);
    return Array.isArray(projects) ? projects : [];
  } catch (error) {
    console.error('Error fetching projects', error);
    return [];
  }
};

export const createProjectRequest = async (project: Partial<IProject>, projectReadme: string): Promise<IProject> => {
  const newProject = await zeusSdk.projects.create(project);

  if (projectReadme) {
    await zeusSdk.projects.createChild(newProject.id, 'readme', {
      content: projectReadme,
    });
  }

  return newProject as IProject;
};

export const updateProjectRequest = async (projectId: string, project: Partial<IProject>): Promise<IProject> => {
  return (await zeusSdk.projects.patch(projectId, project)) as IProject;
};

/**
 * Fetch project readme
 */
export const requestProjectReadme = async (projectId: string): Promise<string> => {
  try {
    const { content } = await zeusSdk.projects.getChild(projectId, 'readme');
    return content || '';
  } catch (error) {
    console.error(`Error fetching project readme for ${projectId}`, error);
    return '';
  }
};

/**
 * Update project readme
 */
export const updateProjectReadmeRequest = async (projectId: string, content: string): Promise<IReadme> => {
  return (await zeusSdk.projects.updateChild(projectId, 'readme', { content: content })) as IReadme;
};

/**
 * Fetch project readme versions
 */
export const requestProjectReadmeVersions = async (
  projectId: string,
  params: IApiRequestOptions = {},
): Promise<IReadmeVersion[]> => {
  const options = {
    ...params,
    include: 'editor',
    queryParameters: {
      sortDirection: 'DESC',
      ...params.queryParameters,
    },
  };
  const response = await zeusSdk.projects.getChild(projectId, 'readme/_versions', options);
  return response.items || [];
};

/**
 * Fetch project history/versions
 */
export const requestProjectHistory = async (
  projectId: string,
  params: IApiRequestOptions = {},
): Promise<IListResponse<IHistoryItem<IProject>>> => {
  const options = {
    ...params,
    queryParameters: {
      limit: 20,
      sortDirection: 'DESC',
      ...params.queryParameters,
    },
  };
  return (await zeusSdk.projects.getChild(projectId, '_versions', options)) as IListResponse<IHistoryItem<IProject>>;
};

/**
 * Fetch project settings
 */
export const fetchProjectSettings = async (projectId: string): Promise<any> => {
  try {
    const settings = await zeusSdk.projects.getSettings(projectId);
    return settings || {};
  } catch (error: any) {
    if (error.status === 404) {
      return {};
    }
    console.error('Error fetching project settings', error);
    throw error;
  }
};

export const fetchProjectQaQueue = async (projectId: string): Promise<any[]> => {
  try {
    return await zeusSdk.projects.getQAQueue(projectId);
  } catch (error) {
    console.error('Error fetching project QA queue', error);
    return [];
  }
};

export const fetchProjectFixQueue = async (projectId: string): Promise<any[]> => {
  try {
    return await zeusSdk.projects.getFixQueue(projectId);
  } catch (error) {
    console.error('Error fetching project fix queue', error);
    return [];
  }
};

export const fetchProjectMyQA = async (projectId: string): Promise<any[]> => {
  try {
    return await zeusSdk.projects.getMyQA(projectId);
  } catch (error) {
    console.error('Error fetching my QA', error);
    return [];
  }
};

export const fetchProjectMyFixing = async (projectId: string): Promise<any[]> => {
  try {
    return await zeusSdk.projects.getMyFixing(projectId);
  } catch (error) {
    console.error('Error fetching my fixing', error);
    return [];
  }
};

/**
 * Save project settings (create or update)
 */
export const saveProjectSettings = async (projectId: string, settings: any, isNew: boolean): Promise<any> => {
  if (isNew) {
    return zeusSdk.projects.addSettings(projectId, settings);
  } else {
    return zeusSdk.projects.updateSettings(projectId, settings);
  }
};
